import { logger } from '@neptune/shared/core-logger-util';
import { type Widget, type WidgetLayoutSettings } from '@neptune/shared/widgets-domain';

import { WidgetDefinition } from '../core/domain/widget-definition';

export type SectionWidget = Widget & {
  type: 'section';
  options?: SectionWidgetOptions;
};

export type SectionWidgetOptions = {
  folded?: boolean;
  layoutSettings?: WidgetLayoutSettings;
  parentSectionId?: string;
};

export function isSectionWidget(widget: Widget | undefined): widget is SectionWidget {
  return widget?.type === 'section';
}

export type SectionsFoldUpdate = Record<string, boolean>;

export function applySectionsFoldUpdate(widgets: Widget[], sectionsFoldUpdate: SectionsFoldUpdate) {
  return widgets.map((widget) => {
    if (
      !isSectionWidget(widget) ||
      sectionsFoldUpdate[widget.id] === undefined ||
      sectionsFoldUpdate[widget.id] === widget.options?.folded
    ) {
      return widget;
    }

    return {
      ...widget,
      options: {
        ...widget.options,
        folded: sectionsFoldUpdate[widget.id],
      },
    };
  });
}

export function sectionWidgetOptionsFromApiToDomain(
  options?: Record<string, any>,
): SectionWidgetOptions {
  const storedParentSectionId = options?.parentSectionId;
  const parentSectionId =
    typeof storedParentSectionId === 'string' && storedParentSectionId.length > 0
      ? storedParentSectionId
      : undefined;

  if (storedParentSectionId !== undefined && parentSectionId === undefined) {
    logger.logMessage(
      'Ignoring malformed stored section parentSectionId; rendering the section at the top level.',
      { module: 'section-widget-options' },
    );
  }

  return {
    folded: !!options?.folded,
    layoutSettings: options?.layoutSettings,
    ...(parentSectionId !== undefined ? { parentSectionId } : {}),
  };
}

export function sectionWidgetOptionsFromDomainToApi(
  options?: SectionWidgetOptions,
): Record<string, any> {
  return {
    folded: !!options?.folded,
    layoutSettings: options?.layoutSettings,
    ...(options?.parentSectionId !== undefined ? { parentSectionId: options.parentSectionId } : {}),
  };
}

export const sectionWidgetDefinition: WidgetDefinition = {
  type: 'section',
  fromApiToDomain: sectionWidgetOptionsFromApiToDomain,
  fromDomainToApi: sectionWidgetOptionsFromDomainToApi,
};
